import type { Config } from "dompurify";
import type { OwaSanitizerConfig } from "../owaSanitizerConfig";
import type { ISanitizerConfigAdapter, DomPurifyConfigForReturnType, } from "./ISanitizerConfigAdapter";
import { getSanitizerConfigKey } from "./sanitizerConfigKey";
import { SanitizerReturnType } from "../sanitizerReturnType";
import LRUCache from "../LRUCache";
import { OWA_DEFAULT_ATTRIBUTES, OWA_DEFAULT_TAGS, OWA_FORBIDDEN_ATTRIBUTES, } from "../sanitizerDefaults";

/**
 * Bound each memo, counting all config/return-mode combinations for DOMPurify.
 */
const CONFIG_MEMO_SIZE = 32;

/**
 * Concrete adapter that translates OwaSanitizerConfig to both
 * DOMPurify Config and native SanitizerConfig formats.
 *
 * Follows the Adapter Design Pattern — a single class implements
 * ISanitizerConfigAdapter with two target-specific methods.
 *
 * Both translations are memoized by config key, plus return mode for DOMPurify.
 * The memos are instance state rather than module state so that constructing
 * a new adapter yields a clean cache, keeping tests isolated from one another.
 */
export class SanitizerConfigAdapter implements ISanitizerConfigAdapter {
    // Created on first use rather than in a field initializer, matching the lazy
    // singleton below — class field initializers run at module-evaluation time.
    private domPurifyConfigMemo: LRUCache<Config> | undefined;
    private nativeSanitizerMemo: LRUCache<Sanitizer> | undefined;
    
    /**
     * Converts an OwaSanitizerConfig to a DOMPurify Config.
     *
     * Define the base configuration with the html profile, then apply additions/removals based on the provided config.:
     * - USE_PROFILES: { html: true } as baseline
     * - ADD_TAGS/ADD_ATTR for additions
     * - FORBID_TAGS/FORBID_ATTR for removals
     *
     * The returned object is memoized and therefore shared between callers.
     * Only the DOMPurify wrapper refreshes the shared URI regexp in place.
     * Return-type flags are set before caching, with a separate entry per mode.
     */
    toDomPurifyConfig(config: OwaSanitizerConfig): Config;
    toDomPurifyConfig<T extends SanitizerReturnType>(config: OwaSanitizerConfig, returnType: T): DomPurifyConfigForReturnType<T>;
    toDomPurifyConfig(config: OwaSanitizerConfig, returnType: SanitizerReturnType = SanitizerReturnType.String): Config {
        const memo = (this.domPurifyConfigMemo ??= new LRUCache<Config>(CONFIG_MEMO_SIZE));
        const mode = returnType === SanitizerReturnType.Node ? SanitizerReturnType.HTMLElement : returnType;
        const configKey = `${getSanitizerConfigKey(config)}:${mode}`;
        const memoized = memo.get(configKey);
        if (memoized) {
            return memoized;
        }
        
        const domPurifyConfig = this.buildDomPurifyConfig(config);
        switch (mode) {
            case SanitizerReturnType.HTMLElement:
                domPurifyConfig.RETURN_DOM = true;
                break;
            case SanitizerReturnType.DocumentFragment:
                domPurifyConfig.RETURN_DOM_FRAGMENT = true;
                break;
            case SanitizerReturnType.TrustedHTML:
                domPurifyConfig.RETURN_TRUSTED_TYPE = true;
                break;
        }
        memo.set(configKey, domPurifyConfig);
        return domPurifyConfig;
    }
    
    /**
     * Converts an OwaSanitizerConfig to a native Sanitizer instance.
     *
     * Starts from the default Sanitizer, then applies incremental additions and
     * removals. The Sanitizer object auto-normalizes conflicts (e.g. an element
     * in both allow and remove lists).
     *
     * The returned instance is memoized and therefore shared between callers.
     * This is sound only because the instance is never mutated after
     * construction: `setHTML()` is specified to filter its input without
     * modifying the Sanitizer passed to it, and `removeUnsafe()` — the one
     * mutating method OWA used to call — is deliberately not called here.
     * Never mutate a Sanitizer returned by this method; a single mutation would
     * silently corrupt the config for every other caller sharing the entry.
     */
    toNativeSanitizerConfig(config: OwaSanitizerConfig): Sanitizer {
        const memo = (this.nativeSanitizerMemo ??= new LRUCache<Sanitizer>(CONFIG_MEMO_SIZE));
        const configKey = getSanitizerConfigKey(config);
        const memoized = memo.get(configKey);
        if (memoized) {
            return memoized;
        }
        
        const sanitizer = this.buildNativeSanitizerConfig(config);
        memo.set(configKey, sanitizer);
        return sanitizer;
    }
    
    /**
     * Builds the DOMPurify config. Called only on a memo miss.
     */
    private buildDomPurifyConfig(config: OwaSanitizerConfig): Config {
        const domPurifyConfig: Config = {
            USE_PROFILES: config.svgProfile ? { html: true, svg: true } : { html: true },
        };
        
        if (config.additionalAttributes?.length) {
            const attributes = new Set(OWA_DEFAULT_ATTRIBUTES);
            for (const attribute of config.additionalAttributes) {
                attributes.add(attribute);
            }
            domPurifyConfig.ADD_ATTR = Array.from(attributes);
        }
        else {
            // Defaults are already unique; avoid building a Set on this path.
            domPurifyConfig.ADD_ATTR = OWA_DEFAULT_ATTRIBUTES.slice();
        }
        
        // Snapshot caller-owned lists so later mutations cannot change a memo entry.
        if (config.additionalTags?.length) {
            domPurifyConfig.ADD_TAGS = config.additionalTags.slice();
        }
        
        // Always forbid OWA_FORBIDDEN_ATTRIBUTES (security-critical)
        const forbiddenAttrs = OWA_FORBIDDEN_ATTRIBUTES.slice();
        if (config.forbiddenAttributes) {
            for (const attribute of config.forbiddenAttributes) {
                forbiddenAttrs.push(attribute);
            }
        }
        if (forbiddenAttrs.length > 0) {
            domPurifyConfig.FORBID_ATTR = forbiddenAttrs;
        }
        
        // DOMPurify's KEEP_CONTENT (default) hoists the children of a FORBID_TAGS element,
        // which is the unwrap behavior; listing them also overrides any allowance.
        if (config.forbiddenTags?.length || config.unwrapTags?.length) {
            domPurifyConfig.FORBID_TAGS = [
                ...(config.forbiddenTags ?? []),
                ...(config.unwrapTags ?? []),
            ];
        }
        
        if (config.allowUnknownProtocols) {
            domPurifyConfig.ALLOW_UNKNOWN_PROTOCOLS = true;
        }
        
        if (config.allowDataAttributes === false) {
            domPurifyConfig.ALLOW_DATA_ATTR = false;
        }
        
        if (config.wholeDocument) {
            domPurifyConfig.WHOLE_DOCUMENT = true;
        }
        
        if (config.forceBody) {
            domPurifyConfig.FORCE_BODY = true;
        }
        
        if (config.additionalUriSafeAttributes?.length) {
            domPurifyConfig.ADD_URI_SAFE_ATTR = config.additionalUriSafeAttributes.slice();
        }
        
        return domPurifyConfig;
    }
    
    /**
     * Builds the native Sanitizer instance. Called only on a memo miss.
     *
     * Starts from the default Sanitizer, then applies incremental additions
     * and removals. The Sanitizer object auto-normalizes conflicts
     * (e.g. an element in both allow and remove lists).
     */
    private buildNativeSanitizerConfig(config: OwaSanitizerConfig): Sanitizer {
        const sanitizer = new Sanitizer();
        
        // OWA-specific elements always added
        for (const tag of OWA_DEFAULT_TAGS) {
            sanitizer.allowElement(tag);
        }
        
        // OWA-specific attributes always added
        for (const attr of OWA_DEFAULT_ATTRIBUTES) {
            sanitizer.allowAttribute(attr);
        }
        
        // Caller additions
        for (const tag of config.additionalTags ?? []) {
            sanitizer.allowElement(tag.toLowerCase());
        }
        for (const attr of config.additionalAttributes ?? []) {
            sanitizer.allowAttribute(attr.toLowerCase());
        }
        
        // Applied after additions so unwrapping wins, and before removals so removal wins.
        for (const tag of config.unwrapTags ?? []) {
            sanitizer.replaceElementWithChildren(tag.toLowerCase());
        }
        
        // Caller removals (auto-normalized by Sanitizer)
        for (const tag of config.forbiddenTags ?? []) {
            sanitizer.removeElement(tag.toLowerCase());
        }
        
        // Always forbid OWA_FORBIDDEN_ATTRIBUTES (security-critical)
        for (const attr of OWA_FORBIDDEN_ATTRIBUTES) {
            sanitizer.removeAttribute(attr.toLowerCase());
        }
        
        // Caller-specific forbidden attributes
        for (const attr of config.forbiddenAttributes ?? []) {
            sanitizer.removeAttribute(attr.toLowerCase());
        }
        
        // Options
        // Default data-* to true — matches DOMPurify's ALLOW_DATA_ATTR default and is required
        // for OWA email bodies which use data-imagetype, data-custom, data-outlook-trace, etc.
        sanitizer.setComments(config.allowComments ?? false);
        sanitizer.setDataAttributes(config.allowDataAttributes ?? true);
        
        return sanitizer;
    }
}

/** Lazily-initialized singleton — avoids module-scope constructor call. */
let _instance: SanitizerConfigAdapter | undefined;

export function getSanitizerConfigAdapter(): SanitizerConfigAdapter {
    if (!_instance) {
        _instance = new SanitizerConfigAdapter();
    }
    return _instance;
}
